<!--
  Interart - Busca dentro da conversa (Ctrl+Shift+F).
  Destino no fonte: /opt/chatwoot-src/app/javascript/dashboard/components/widgets/conversation/ConversationFind.vue
  Montado dentro de MessagesView.vue (dono do scroll container .conversation-panel).

  Comportamento:
  - Ctrl+Shift+F (ou Cmd+Shift+F) abre a caixinha e foca o input.
  - Ao buscar: carrega TODO o historico da conversa (store: fetchPreviousMessages ate getAllMessagesLoaded),
    destaca os matches em verde nos baloes, mostra contador "atual / total".
  - Comeca na ocorrencia MAIS RECENTE. Enter/Up -> mais antiga; Down -> mais nova (com wrap). Esc fecha.
  - Destaque feito envolvendo os text-nodes ja renderizados (preserva links/emojis/markdown). Limpa ao fechar.
-->
<script setup>
import { ref, nextTick, watch, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

const store = useStore();
const { t } = useI18n();

const HIT_CLASS = 'interart-find-hit';
const ACTIVE_CLASS = 'interart-find-hit--active';

const visible = ref(false);
const query = ref('');
const loading = ref(false);
const activeIndex = ref(-1);
const total = ref(0);
const inputRef = ref(null);

let hits = []; // <span.interart-find-hit> em ordem cronologica (topo=antigo -> fim=recente)
let debounceTimer = null;

function getPanel() {
  return document.querySelector('.conversation-panel');
}

function getSelectedChatId() {
  const chat = store.getters.getSelectedChat;
  return chat && chat.id ? chat.id : null;
}

function clearHighlights() {
  const panel = getPanel();
  if (panel) {
    panel.querySelectorAll('span.' + HIT_CLASS).forEach(span => {
      const parent = span.parentNode;
      if (!parent) return;
      parent.replaceChild(document.createTextNode(span.textContent), span);
      parent.normalize();
    });
  }
  hits = [];
}

function buildHighlights(rawQuery) {
  clearHighlights();
  const panel = getPanel();
  const needle = (rawQuery || '').toLowerCase();
  if (!panel || !needle) return [];

  const walker = document.createTreeWalker(panel, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const val = node.nodeValue;
      if (!val || !val.trim()) return NodeFilter.FILTER_REJECT;
      const p = node.parentElement;
      if (!p) return NodeFilter.FILTER_REJECT;
      if (p.tagName === 'SCRIPT' || p.tagName === 'STYLE') return NodeFilter.FILTER_REJECT;
      if (!p.closest('.message-bubble-container')) return NodeFilter.FILTER_REJECT;
      return val.toLowerCase().includes(needle)
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT;
    },
  });

  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);

  const collected = [];
  nodes.forEach(node => {
    const text = node.nodeValue;
    const lower = text.toLowerCase();
    let idx = lower.indexOf(needle);
    if (idx === -1) return;
    const frag = document.createDocumentFragment();
    let from = 0;
    while (idx !== -1) {
      if (idx > from) frag.appendChild(document.createTextNode(text.slice(from, idx)));
      const span = document.createElement('span');
      span.className = HIT_CLASS;
      span.textContent = text.slice(idx, idx + needle.length);
      frag.appendChild(span);
      collected.push(span);
      from = idx + needle.length;
      idx = lower.indexOf(needle, from);
    }
    if (from < text.length) frag.appendChild(document.createTextNode(text.slice(from)));
    if (node.parentNode) node.parentNode.replaceChild(frag, node);
  });
  return collected;
}

async function loadEntireHistory() {
  const convId = getSelectedChatId();
  if (!convId) return;
  for (let i = 0; i < 1000; i += 1) {
    if (store.getters.getAllMessagesLoaded) break;
    const msgs = store.getters.getSelectedChat.messages;
    if (!msgs || !msgs.length) break;
    const beforeId = msgs[0].id;
    // eslint-disable-next-line no-await-in-loop
    await store.dispatch('fetchPreviousMessages', {
      conversationId: convId,
      before: beforeId,
    });
    const after = store.getters.getSelectedChat.messages;
    // guard: cursor nao avancou e flag nao virou -> para (evita loop)
    if (after && after[0] && after[0].id === beforeId && !store.getters.getAllMessagesLoaded) {
      break;
    }
  }
}

function updateActive(scroll) {
  hits.forEach(s => s.classList.remove(ACTIVE_CLASS));
  const el = hits[activeIndex.value];
  if (!el) return;
  el.classList.add(ACTIVE_CLASS);
  if (scroll) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

async function runSearch() {
  const q = query.value.trim();
  if (!q) {
    clearHighlights();
    total.value = 0;
    activeIndex.value = -1;
    return;
  }
  loading.value = true;
  try {
    await loadEntireHistory();
    await nextTick();
    // deixa o DOM assentar apos os prepends de historico
    await new Promise(resolve => setTimeout(resolve, 60));
    hits = buildHighlights(q);
    total.value = hits.length;
    activeIndex.value = hits.length ? hits.length - 1 : -1; // comeca na MAIS RECENTE
    updateActive(true);
  } finally {
    loading.value = false;
  }
}

function navigate(dir) {
  if (!hits.length) return;
  const n = hits.length;
  activeIndex.value = (activeIndex.value + dir + n) % n;
  updateActive(true);
}

function onInput() {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(runSearch, 300);
}

function open(e) {
  if (e && typeof e.preventDefault === 'function') e.preventDefault();
  if (!getSelectedChatId()) return;
  visible.value = true;
  nextTick(() => {
    if (inputRef.value) {
      inputRef.value.focus();
      inputRef.value.select();
    }
    if (query.value.trim()) runSearch();
  });
}

function close() {
  visible.value = false;
  clearHighlights();
  total.value = 0;
  activeIndex.value = -1;
}

useKeyboardEvents({
  '$mod+Shift+KeyF': { action: open, allowOnFocusedInput: true },
});

// fecha/limpa ao trocar de conversa
watch(
  () => getSelectedChatId(),
  () => {
    if (visible.value) close();
  }
);

onBeforeUnmount(() => {
  clearHighlights();
  clearTimeout(debounceTimer);
});
</script>

<template>
  <div v-if="visible" class="interart-find-box">
    <input
      ref="inputRef"
      v-model="query"
      type="text"
      class="interart-find-input"
      :placeholder="t('CONVERSATION.FIND_IN_CONVERSATION.PLACEHOLDER')"
      @input="onInput"
      @keydown.enter.prevent="navigate(-1)"
      @keydown.up.prevent="navigate(-1)"
      @keydown.down.prevent="navigate(1)"
      @keydown.esc.prevent.stop="close"
    />
    <span class="interart-find-counter">
      <template v-if="loading">…</template>
      <template v-else-if="total">{{ activeIndex + 1 }} / {{ total }}</template>
      <template v-else-if="query.trim()">{{ t('CONVERSATION.FIND_IN_CONVERSATION.NO_RESULTS') }}</template>
    </span>
    <button
      type="button"
      class="interart-find-btn"
      :title="t('CONVERSATION.FIND_IN_CONVERSATION.PREVIOUS')"
      @click="navigate(-1)"
    >
      ↑
    </button>
    <button
      type="button"
      class="interart-find-btn"
      :title="t('CONVERSATION.FIND_IN_CONVERSATION.NEXT')"
      @click="navigate(1)"
    >
      ↓
    </button>
    <button type="button" class="interart-find-btn" title="Esc" @click="close">✕</button>
  </div>
</template>

<style>
/* nao-scoped: os destaques vivem nos baloes (fora deste SFC) */
.interart-find-box {
  position: absolute;
  top: 10px;
  right: 18px;
  z-index: 50;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.16);
}
.interart-find-input {
  border: none;
  outline: none;
  font-size: 13px;
  padding: 4px 6px;
  min-width: 220px;
  background: transparent;
  color: #111827;
}
.interart-find-counter {
  font-size: 12px;
  color: #64748b;
  min-width: 52px;
  text-align: center;
  white-space: nowrap;
}
.interart-find-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  line-height: 1;
  padding: 4px 7px;
  border-radius: 6px;
  color: #475569;
}
.interart-find-btn:hover {
  background: rgba(0, 0, 0, 0.06);
}
.interart-find-hit {
  background: #b9f6ca;
  color: #064e3b;
  border-radius: 2px;
}
.interart-find-hit--active {
  background: #00c853;
  color: #ffffff;
  box-shadow: 0 0 0 2px rgba(0, 200, 83, 0.45);
}
</style>
